import { EmeraldButton } from "@emerald-react/button";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldInput } from "@emerald-react/input";
import { EmeraldModal } from "@emerald-react/modal";
import { EmeraldButtonSize, EmeraldButtonVariant } from "@emerald/nxcore";
import type { SurveyPOICategory } from "@survey-types/survey.poi.types";
import { BaseSyntheticEvent, useState } from "react";

interface RenameCategoryModalProps {
  open: boolean;
  onClose: () => void;
  onRename: (categoryId: string, newName: string) => Promise<void>;
  category: SurveyPOICategory | null;
  existingCategories: SurveyPOICategory[];
  isLoading?: boolean;
}

const RenameCategoryModal = ({
  open,
  onClose,
  onRename,
  category,
  existingCategories,
  isLoading = false,
}: RenameCategoryModalProps) => {
  // Initialised with the current category name via lazy initialiser.
  // The parent should mount this component with key={category?.extId} so state
  // resets automatically when the category being renamed changes.
  const [categoryName, setCategoryName] = useState<string>(
    () => category?.categoryName ?? "",
  );
  const [categoryNameError, setCategoryNameError] = useState<string>("");

  const originalName = category?.categoryName ?? "";
  const trimmedName = categoryName.trim();

  const noChanges = trimmedName === originalName.trim();

  // Duplicate check: case-sensitive against OTHER categories only.
  // Different capitalisation (e.g. "amazon Office Store" vs "Amazon Office Store")
  // is treated as a distinct name and is therefore allowed.
  const otherCategories = existingCategories.filter(
    (c) => c.extId !== category?.extId,
  );
  const isDuplicate = otherCategories.some(
    (c) => c.categoryName === trimmedName,
  );

  const isTooShort = trimmedName.length > 0 && trimmedName.length < 2;
  const isTooLong = trimmedName.length > 50;

  const isFormValid =
    trimmedName.length >= 2 &&
    !isTooLong &&
    !noChanges &&
    !isDuplicate &&
    !categoryNameError;

  const handleCategoryNameChange = (value: string) => {
    setCategoryName(value);
    setCategoryNameError("");
  };

  const handleSubmit = async () => {
    if (!isFormValid || !category) {
      return;
    }
    await onRename(category.extId, trimmedName);
  };

  const handleCancel = () => {
    setCategoryName("");
    setCategoryNameError("");
    onClose();
  };

  const getHelperText = (): string => {
    if (isTooShort) return "Category name must be at least 2 characters";
    if (isTooLong) return "Category name must be less than 50 characters";
    if (isDuplicate)
      return "This Category name is already in use. Please choose a different name.";
    if (categoryNameError) return categoryNameError;
    return "";
  };

  const helperText = getHelperText();

  return (
    <EmeraldModal
      opened={open}
      onClose={handleCancel}
      headerText="Rename category"
      hasCloseIcon
      zIndex={1000}
    >
      <div className="grid grid-flow-row gap-4 p-2">
        {/* Info banner */}
        <div className="flex items-start gap-2 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2">
          <EmeraldIcon
            icon="info"
            variant="icon"
            fontsizeInRem={1.1}
            className="mt-0.5 shrink-0 text-amber-600"
          />
          <span className="text-sm text-amber-800">
            All records with this category name will be updated.
          </span>
        </div>

        <EmeraldInput
          label="Category name"
          placeholder="Enter category name"
          value={categoryName}
          onChange={(e: BaseSyntheticEvent) =>
            handleCategoryNameChange(e.target.value)
          }
          maxLength={50}
        />

        {/* Error / validation message */}
        {helperText && (
          <p className="mt-1 text-sm text-red-600">{helperText}</p>
        )}

        {/* No-changes status */}
        {noChanges && !isTooShort && trimmedName.length > 0 && (
          <p className="mt-1 text-sm text-red-600">
            No changes made.
          </p>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <EmeraldButton
            label="Cancel"
            variant={EmeraldButtonVariant.TextButton}
            size={EmeraldButtonSize.Medium}
            onClick={handleCancel}
            disabled={isLoading}
            nativeProps={{ type: "button" }}
          />
          <EmeraldButton
            label="Save"
            variant={EmeraldButtonVariant.Primary}
            size={EmeraldButtonSize.Medium}
            onClick={handleSubmit}
            disabled={!isFormValid || isLoading}
            nativeProps={{ type: "button" }}
          />
        </div>

        <p className="text-xs text-slate-400">
          Try typing &quot;DT&quot; or &quot;Electronics&quot; to see the
          duplicate check, or a single character to see the length check.
        </p>
      </div>
    </EmeraldModal>
  );
};

export default RenameCategoryModal;
